"use client";
import type React from "react";
import { useEffect, useLayoutEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { twMerge } from "tailwind-merge";
import { clsx, type ClassValue } from "clsx";

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// useLayoutEffect warns during SSR; this component only measures on the client
// (it renders null until open), so fall back to useEffect on the server.
const useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;

// Gap between the trigger and the menu, and the minimum breathing room we leave
// against the viewport edge so a capped menu never touches the screen border.
const TRIGGER_GAP = 8;
const VIEWPORT_MARGIN = 8;
// For the portal (flip-capable) path: only flip a menu above its trigger when
// the space below is genuinely cramped AND there's more room above — otherwise
// keep the default "open downward" behavior and just let the menu scroll.
const MIN_SPACE_BELOW = 160;

interface DropdownProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
  triggerRef?: React.RefObject<HTMLElement>;
}

export const Dropdown: React.FC<DropdownProps> = ({
  isOpen,
  onClose,
  children,
  className = "",
  triggerRef,
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number | null;
    bottom: number | null;
    left: number;
    width: number;
  }>({ top: 0, bottom: null, left: 0, width: 0 });
  const [usePortal, setUsePortal] = useState(false);
  const [isReady, setIsReady] = useState(false);
  // Viewport-fit cap (px). When the menu would be taller than the room available
  // where it opens, we cap it and let it scroll instead of running off-screen.
  // null = no cap needed. Harmless on tall screens (the cap exceeds the content,
  // so overflow:auto shows no scrollbar) — it only activates on short screens.
  const [maxHeight, setMaxHeight] = useState<number | null>(null);

  // Check if we're inside a container with overflow constraints (like a table)
  const isInConstrainedContainer = useCallback(() => {
    if (!triggerRef?.current) return false;

    let element = triggerRef.current.parentElement;
    while (element && element !== document.body) {
      const computedStyle = window.getComputedStyle(element);
      const overflow = computedStyle.getPropertyValue('overflow');
      const overflowX = computedStyle.getPropertyValue('overflow-x');
      const overflowY = computedStyle.getPropertyValue('overflow-y');

      // Check for overflow constraints that could clip dropdowns
      if (
        overflow === 'hidden' || overflow === 'auto' || overflow === 'scroll' ||
        overflowX === 'hidden' || overflowX === 'auto' || overflowX === 'scroll' ||
        overflowY === 'hidden' || overflowY === 'auto' || overflowY === 'scroll'
      ) {
        return true;
      }

      element = element.parentElement;
    }
    return false;
  }, [triggerRef]);

  // Calculate position + viewport-fit height for portal (fixed-position) rendering.
  // Prefers opening below the trigger; flips above only when below is cramped and
  // above has more room. Either way the menu is capped to the space it has so a
  // long list scrolls within the viewport instead of being clipped off-screen.
  const calculatePosition = useCallback(() => {
    if (!triggerRef?.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const dropdownWidth = 192; // w-48 = 192px
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Horizontal: right-aligned by default, clamped to the viewport edges.
    let left = rect.right - dropdownWidth;
    if (left < VIEWPORT_MARGIN) left = VIEWPORT_MARGIN;
    if (left + dropdownWidth > viewportWidth - VIEWPORT_MARGIN) {
      left = viewportWidth - dropdownWidth - VIEWPORT_MARGIN;
    }

    const spaceBelow = viewportHeight - rect.bottom - TRIGGER_GAP - VIEWPORT_MARGIN;
    const spaceAbove = rect.top - TRIGGER_GAP - VIEWPORT_MARGIN;

    if (spaceBelow >= MIN_SPACE_BELOW || spaceBelow >= spaceAbove) {
      // Open downward, anchored under the trigger.
      setDropdownPosition({ top: rect.bottom + TRIGGER_GAP, bottom: null, left, width: dropdownWidth });
      setMaxHeight(Math.max(spaceBelow, 0));
    } else {
      // Flip upward, anchored to the trigger's top edge via `bottom` so the menu
      // grows up from there and its height is bounded by the room above.
      setDropdownPosition({ top: null, bottom: viewportHeight - rect.top + TRIGGER_GAP, left, width: dropdownWidth });
      setMaxHeight(Math.max(spaceAbove, 0));
    }
  }, [triggerRef]);

  // Determine if we should use portal rendering
  useEffect(() => {
    if (isOpen) {
      if (triggerRef) {
        const shouldUsePortal = isInConstrainedContainer();
        setUsePortal(shouldUsePortal);

        if (shouldUsePortal) {
          calculatePosition();
          // Small delay to ensure position is calculated before showing to prevent jumps
          const timeout = setTimeout(() => setIsReady(true), 0);
          return () => clearTimeout(timeout);
        } else {
          setIsReady(true);
        }
      } else {
        setUsePortal(false);
        setIsReady(true);
      }
    } else {
      setIsReady(false);
      // Recompute fresh on the next open rather than reusing a stale cap.
      setMaxHeight(null);
    }
  }, [isOpen, triggerRef, isInConstrainedContainer, calculatePosition]);

  // Recalculate position on scroll/resize when using portal
  useEffect(() => {
    if (!isOpen || !usePortal) return;

    const handlePositionUpdate = () => {
      calculatePosition();
    };

    window.addEventListener('scroll', handlePositionUpdate, true);
    window.addEventListener('resize', handlePositionUpdate);

    return () => {
      window.removeEventListener('scroll', handlePositionUpdate, true);
      window.removeEventListener('resize', handlePositionUpdate);
    };
  }, [isOpen, usePortal, calculatePosition]);

  // Non-portal (absolute) path: it's positioned by CSS, so measure the rendered
  // top edge and cap the height to whatever room remains below it in the
  // viewport. Keeps long menus (e.g. a tall account menu on an iPhone SE)
  // scrollable on-screen instead of clipped. Re-measures on resize/scroll.
  useIsomorphicLayoutEffect(() => {
    if (!isOpen || !isReady || usePortal) return;

    const measure = () => {
      const el = dropdownRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      const fit = window.innerHeight - top - VIEWPORT_MARGIN;
      setMaxHeight(fit > 0 ? fit : null);
    };

    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [isOpen, isReady, usePortal]);

  // Outside-press dismissal. Two escape hatches beyond this menu's own subtree:
  // `.dropdown-toggle` on the trigger (so tapping it toggles rather than
  // close-then-reopen), and `.dropdown-content` for anything that belongs to an
  // open menu but sits elsewhere in the DOM — a portalled dialog opened from a
  // menu row, say. Note the check is GLOBAL: an element carrying
  // `.dropdown-content` suppresses this for every mounted Dropdown, so only put
  // it on something modal or genuinely menu-owned.
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(target) &&
        !target.closest('.dropdown-toggle') &&
        !target.closest('.dropdown-content')
      ) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  if (!isOpen || !isReady) return null;

  // Inline cap shared by both render paths. overflow:auto only shows a scrollbar
  // when the content actually exceeds maxHeight, so this is a no-op on tall
  // screens and only kicks in to prevent clipping on short ones.
  const fitStyle: React.CSSProperties = maxHeight != null
    ? { maxHeight, overflowY: 'auto', overscrollBehavior: 'contain' }
    : {};

  // Use portal rendering if we're in a constrained container
  if (usePortal && typeof document !== 'undefined') {
    return createPortal(
      <div
        ref={dropdownRef}
        // z-[9999999] sits above Modal's z-[999999] so a dropdown opened
        // from inside a modal (e.g., the More menu in VideoPlayerModal's
        // mobile footer) renders above the modal backdrop instead of
        // getting hidden behind it. Dropdowns should always sit above the
        // surface that triggered them.
        className={cn(
          "fixed z-[9999999] rounded-xl border border-gray-200 bg-white shadow-theme-lg dark:border-gray-800 dark:bg-gray-dark dropdown-content",
          className
        )}
        style={{
          ...(dropdownPosition.top != null ? { top: dropdownPosition.top } : {}),
          ...(dropdownPosition.bottom != null ? { bottom: dropdownPosition.bottom } : {}),
          left: dropdownPosition.left,
          minWidth: dropdownPosition.width,
          width: 'max-content',
          ...fitStyle,
        }}
        role="menu"
        aria-orientation="vertical"
      >
        {children}
      </div>,
      document.body
    );
  }

  return (
    <div
      ref={dropdownRef}
      className={cn(
        "absolute z-50 right-0 mt-2 rounded-xl border border-gray-200 bg-white shadow-theme-lg dark:border-gray-800 dark:bg-gray-dark dropdown-content",
        className
      )}
      style={fitStyle}
      role="menu"
      aria-orientation="vertical"
    >
      {children}
    </div>
  );
};
